import { Button, cx, Selectß } from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Security2FAType } from '@/shared/types/auth';
import { DEFAULT_SELECTED_TWO_FACTOR_AUTH_METHOD } from '../constants';
import { dataTestIds } from '../dataTestIds';
import { useAvailableTwoFactorAuthMethods } from '../hooks/useAvailableTwoFactorAuthMethods';
import { StateEvent } from '../state-machine-config';

/**
 * The state when the user can choose an authentication method to set up.
 */
export function SelectAuthMethodState({
  isBeforeLogin = false,
  ...props
}: {
  [StateEvent.SELECT_AUTH_APP_METHOD]: () => void;
  [StateEvent.SELECT_SMS_METHOD]: () => void;
  [StateEvent.SELECT_NO_METHOD]: () => void;
  isBeforeLogin?: boolean;
}) {
  const t = useTranslations(
    'settings:security.twoFactorAuthStates.selectAuthMethod',
  );
  const [selectedMethod, setSelectedMethod] = useState<string>(
    DEFAULT_SELECTED_TWO_FACTOR_AUTH_METHOD,
  );
  const { options, isLoading: isLoadingAvailableMethods } =
    useAvailableTwoFactorAuthMethods({
      isBeforeLogin,
    });

  return (
    <div
      className={cx(
        'flex flex-col gap-4 my-4',
        isBeforeLogin ? 'sm:flex-row sm:items-center' : 'flex-col',
      )}
    >
      <div className="font-semibold text-lg">
        {t('chooseAuthenticationMethod')}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Selectß
          data-testid="auth-method-select"
          value={selectedMethod ?? undefined}
          placeholder={t('selectAuthenticationMethod')}
          options={options}
          onChange={setSelectedMethod}
          variant="outlineNeutral"
          version="legacy"
          size="lg"
          disabled={isLoadingAvailableMethods}
          className="min-w-[20em]"
        />
        <div className="flex gap-2">
          <Button
            disabled={!selectedMethod || isLoadingAvailableMethods}
            onClick={() => {
              if (selectedMethod === Security2FAType.AUTHENTICATION_APP) {
                props[StateEvent.SELECT_AUTH_APP_METHOD]();
              } else if (selectedMethod === Security2FAType.SMS) {
                props[StateEvent.SELECT_SMS_METHOD]();
              }
            }}
            size="lg"
            data-testid={dataTestIds.setupSetUpButton}
          >
            {t('setupButton')}
          </Button>
          {!isBeforeLogin && (
            <Button
              variant="outlineNeutral"
              version="legacy"
              size="lg"
              onClick={props[StateEvent.SELECT_NO_METHOD]}
              data-testid={dataTestIds.setupCancelButton}
            >
              {t('cancelButton')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
